import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useIsNative } from "@/hooks/useIsNative";
import { useAuth } from "@/contexts/AuthContext";
import ConnectAuthPage from "@/pages/connect-portal/ConnectAuthPage";
import NativeOnboarding, {
  hasCompletedNativeOnboarding,
} from "@/components/native/NativeOnboarding";
import ConnectHome from "@/pages/connect-portal/ConnectHome";

/**
 * @deprecated Contacts permission is now exclusively requested at point of use
 * inside the Contacts tab (NativeContactsTab) per Apple guidance. This wrapper
 * is kept as a passthrough so existing imports do not break, but it MUST NOT
 * trigger CNContactStore.requestAccess.
 */
export function NativeContactPermissionGate({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/**
 * Mounted at "/".
 *
 * - Web → renders the public ConnectHome marketing page (eager import to avoid
 *         a white-screen flash on first paint — this IS the entry route).
 * - Native (iOS/Android via Capacitor):
 *     • signed in → /connect-portal/dashboard
 *     • signed out, first launch → onboarding carousel
 *     • signed out, returning → /connect-portal/auth
 */
export function NativeRootGate() {
  const isNative = useIsNative();
  const { user, isLoading } = useAuth();

  if (!isNative) {
    return <ConnectHome />;
  }


  if (isLoading) {
    return hasCompletedNativeOnboarding() ? <ConnectAuthPage /> : <NativeOnboarding />;
  }

  if (user) {
    return <Navigate to="/connect-portal/dashboard" replace />;
  }

  if (!hasCompletedNativeOnboarding()) {
    return <NativeOnboarding />;
  }

  return <Navigate to="/connect-portal/auth" replace />;
}

export default NativeRootGate;

